/**
 * The opponents you can play.
 *
 * Prices and ids were read from the live Workers AI catalogue
 * (`wrangler ai models list --json`) on 2026-08-12, sorted by input cost.
 * Re-run that command when refreshing this list — the catalogue moves, and a
 * stale id is a runtime 404 rather than a compile error.
 */
export type BotKind = 'llm' | 'book' | 'heuristic' | 'engine' | 'jev'

export interface BotModel {
  readonly id: string
  readonly label: string
  readonly note: string
  readonly kind: BotKind
  /** USD per million input tokens. Absent for the non-LLM opponents. */
  readonly inputPricePerM?: number
  readonly outputPricePerM?: number
  /**
   * Settings for a `kind: 'engine'` opponent, which runs in the browser.
   *
   * `elo` uses Stockfish's UCI_LimitStrength — an honest cap, not a vibe.
   * Null means unlimited. Stockfish refuses caps below 1320, so every capped
   * entry here must stay at or above that.
   */
  readonly engine?: { readonly elo: number | null; readonly movetimeMs: number }
}

export const BOT_MODELS: readonly BotModel[] = [
  // Jev (typesafe-ai/jev, via Vercel AI Gateway — see apps/worker/src/jev.ts)
  // is not an LLM: it is a structured decision model that picks from a fixed
  // option set rather than free-writing chess. It sees every legal move plus
  // what this account's own history says about them, and chooses. Listed
  // first — it is the featured opponent.
  {
    id: 'jev',
    label: 'Jev',
    note: 'Studies your game history and picks the move it thinks beats you specifically.',
    kind: 'jev',
  },
  {
    id: '@cf/ibm-granite/granite-4.0-h-micro',
    label: 'Granite micro',
    note: 'Cheapest model on the platform. Expect chaos.',
    kind: 'llm',
    inputPricePerM: 0.017,
    outputPricePerM: 0.112,
  },
  {
    id: '@cf/meta/llama-3.2-1b-instruct',
    label: 'Llama 3.2 1B',
    note: 'Tiny. Knows what chess notation looks like, not much else.',
    kind: 'llm',
    inputPricePerM: 0.027,
    outputPricePerM: 0.201,
  },
  {
    id: '@cf/meta/llama-3.2-3b-instruct',
    label: 'Llama 3.2 3B',
    note: 'Three times the size, still hangs its queen.',
    kind: 'llm',
    inputPricePerM: 0.0509,
    outputPricePerM: 0.335,
  },
  {
    id: '@cf/qwen/qwen3-30b-a3b-fp8',
    label: 'Qwen3 30B',
    note: 'Mixture of experts at small-model prices.',
    kind: 'llm',
    inputPricePerM: 0.0509,
    outputPricePerM: 0.335,
  },
  {
    id: '@cf/openai/gpt-oss-20b',
    label: 'GPT-OSS 20B',
    note: 'A different family, for comparison.',
    kind: 'llm',
    inputPricePerM: 0.2,
    outputPricePerM: 0.3,
  },
  {
    id: '@cf/meta/llama-3.3-70b-instruct-fp8-fast',
    label: 'Llama 3.3 70B',
    note: 'The big one. Roughly seventeen times the cost of Granite.',
    kind: 'llm',
    inputPricePerM: 0.293,
    outputPricePerM: 2.253,
  },
  {
    id: 'book',
    label: 'The Crowd',
    note: 'Plays whatever people usually play here. Free, and clueless once out of book.',
    kind: 'book',
  },
  {
    id: 'heuristic',
    label: 'Greedy',
    note: 'No model at all: takes the best capture it can see. The baseline to beat.',
    kind: 'heuristic',
  },
  // The engines run in your browser as a Web Worker, so the games are local to
  // the device: nothing is sent anywhere, and nothing is saved.
  {
    id: 'sf-1350',
    label: 'Stockfish 1350',
    note: 'A real engine on a tight leash. Beatable with care.',
    kind: 'engine',
    engine: { elo: 1350, movetimeMs: 300 },
  },
  {
    id: 'sf-1700',
    label: 'Stockfish 1700',
    note: 'Club-player strength. It will punish loose pieces.',
    kind: 'engine',
    engine: { elo: 1700, movetimeMs: 300 },
  },
  {
    id: 'sf-2200',
    label: 'Stockfish 2200',
    note: 'Master-ish. Expect to lose, and to learn why.',
    kind: 'engine',
    engine: { elo: 2200, movetimeMs: 300 },
  },
  {
    id: 'sf-full',
    label: 'Stockfish',
    note: 'The strongest thing on this site. It will not lose. Runs entirely in your browser.',
    kind: 'engine',
    engine: { elo: null, movetimeMs: 600 },
  },
]

/** Cheapest LLM in the catalogue, which is what a new game gets. */
export const DEFAULT_BOT_MODEL = '@cf/ibm-granite/granite-4.0-h-micro'

export const findBotModel = (id: string): BotModel | null =>
  BOT_MODELS.find((model) => model.id === id) ?? null

export const botLabel = (id: string): string => findBotModel(id)?.label ?? id

export const isKnownBotModel = (id: string): boolean => findBotModel(id) !== null
